/**
 * Resumable download cache. Wraps `download.worker.ts` with a
 * Promise-based API and persists per-file metadata (URL, ETag,
 * expected size) in localStorage so a follow-up session can decide
 * whether the OPFS bytes are still valid before issuing a Range
 * request.
 *
 * Falls back to a plain in-memory streaming fetch in environments
 * that don't expose OPFS in workers (older Safari, private mode in
 * some browsers). The fallback gives no resumption — a tab close
 * drops the bytes — but the rest of the app still works.
 */

export type DownloadProgress = (loaded: number, total: number) => void;

interface StoredMeta {
  url: string;
  etag: string | null;
  expectedBytes: number;
}

const META_KEY = (key: string) => `redact_dl_meta_${key}`;

function safeKey(key: string): string {
  return key.replace(/[^A-Za-z0-9._-]/g, "_");
}

function readMeta(key: string): StoredMeta | null {
  try {
    const v = localStorage.getItem(META_KEY(key));
    return v ? (JSON.parse(v) as StoredMeta) : null;
  } catch {
    return null;
  }
}

function writeMeta(key: string, meta: StoredMeta): void {
  try {
    localStorage.setItem(META_KEY(key), JSON.stringify(meta));
  } catch { /* ignore */ }
}

let workerInstance: Worker | null = null;

interface PendingRequest {
  resolve: (bytes: Uint8Array) => void;
  reject: (err: Error) => void;
  onProgress: DownloadProgress;
  onResolved: (etag: string | null) => void;
}

const pending = new Map<number, PendingRequest>();
let nextReqId = 1;

function getWorker(): Worker {
  if (workerInstance) return workerInstance;
  workerInstance = new Worker(
    new URL("./download.worker.ts", import.meta.url),
    { type: "module" },
  );
  workerInstance.addEventListener("message", (event: MessageEvent) => {
    const msg = event.data as
      | { reqId: number; type: "progress"; loaded: number; total: number }
      | { reqId: number; type: "done"; bytes: ArrayBuffer; etag: string | null }
      | { reqId: number; type: "error"; message: string };
    const req = pending.get(msg.reqId);
    if (!req) return;
    if (msg.type === "progress") {
      req.onProgress(msg.loaded, msg.total);
    } else if (msg.type === "done") {
      pending.delete(msg.reqId);
      req.onResolved(msg.etag);
      req.resolve(new Uint8Array(msg.bytes));
    } else {
      pending.delete(msg.reqId);
      req.reject(new Error(msg.message));
    }
  });
  workerInstance.addEventListener("error", (event: ErrorEvent) => {
    const err = new Error(event.message || "download worker error");
    for (const [id, req] of pending) {
      pending.delete(id);
      req.reject(err);
    }
  });
  return workerInstance;
}

function workerSupported(): boolean {
  return (
    typeof Worker !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.storage?.getDirectory
  );
}

/**
 * Fetch `url` into a `Uint8Array`, persisting the bytes to OPFS so
 * subsequent calls return instantly and an interrupted call can
 * resume from the byte offset already on disk.
 *
 * `key` namespaces the cache entry (use `${variantId}__${file}`).
 * `expectedBytes` lets us short-circuit when the on-disk size already
 * matches and lets us validate that a partial download is still a
 * prefix of the same target.
 */
export async function fetchResumable(
  url: string,
  key: string,
  expectedBytes: number,
  onProgress: DownloadProgress,
): Promise<Uint8Array> {
  if (!workerSupported()) {
    return fetchInMemory(url, expectedBytes, onProgress);
  }

  const stored = readMeta(key);
  // Only carry the previous ETag forward when nothing else has
  // shifted underneath us. A change to the URL or the expected size
  // means the partial bytes are no longer a valid prefix.
  const prevEtag =
    stored && stored.url === url && stored.expectedBytes === expectedBytes
      ? stored.etag
      : null;

  const reqId = nextReqId++;
  const worker = getWorker();
  return new Promise<Uint8Array>((resolve, reject) => {
    pending.set(reqId, {
      resolve,
      reject,
      onProgress,
      onResolved: (etag) => writeMeta(key, { url, etag, expectedBytes }),
    });
    worker.postMessage({
      reqId,
      url,
      dirKey: safeKey(key),
      expectedBytes,
      prevEtag,
    });
  });
}

async function fetchInMemory(
  url: string,
  expectedBytes: number,
  onProgress: DownloadProgress,
): Promise<Uint8Array> {
  const res = await fetch(url);
  if (!res.ok || !res.body) {
    throw new Error(`Fetch ${url} failed: ${res.status}`);
  }
  const total = Number(res.headers.get("content-length") ?? expectedBytes);
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress(loaded, total);
  }
  const out = new Uint8Array(loaded);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.byteLength;
  }
  return out;
}
